import { apiRequest } from '../client'
import type {
  AddItemRequest,
  AddItemResponse,
  BulkAddItemsResponse,
  CreateDailyMenuRequest,
  CreateSectionRequest,
  DailyCreateResult,
  GeneratePosPageResponse,
  Menu,
  MenuContents,
  MenuPrintLink,
  PublishMenuResponse,
  UpdateItemRequest,
  UpdateMenuRequest,
  UpdateSectionRequest,
} from '../types'

interface MenuWeekParams {
  from?: string // ISO date
  to?: string // ISO date
}

interface MenuContentsParams {
  includeInactive?: boolean
  includeDeleted?: boolean
  includePriceStatus?: boolean
}

export const menusApi = {
  // ========== Menu CRUD ==========

  /**
   * List menus for a restaurant. With from+to, returns daily menus in that range
   * (feeds the week view); otherwise returns all non-deleted menus.
   */
  listByRestaurant: (restaurantId: number, params?: MenuWeekParams) => {
    const searchParams = new URLSearchParams()
    if (params?.from) searchParams.set('from', params.from)
    if (params?.to) searchParams.set('to', params.to)
    const query = searchParams.toString()
    return apiRequest<{ items: Array<Menu> }>(
      `/api/v0/admin/restaurants/${restaurantId}/menus${query ? `?${query}` : ''}`,
    )
  },

  /** Get (auto-create on first access) the restaurant's master menu. */
  getMaster: (restaurantId: number) =>
    apiRequest<{ menu: Menu }>(
      `/api/v0/admin/restaurants/${restaurantId}/master`,
    ),

  /** Get full menu contents for editing. */
  getContents: (menuId: number, params?: MenuContentsParams) => {
    const searchParams = new URLSearchParams()
    if (params?.includeInactive) searchParams.set('includeInactive', 'true')
    if (params?.includeDeleted) searchParams.set('includeDeleted', 'true')
    if (params?.includePriceStatus)
      searchParams.set('includePriceStatus', 'true')
    const query = searchParams.toString()
    return apiRequest<MenuContents>(
      `/api/v0/admin/menus/${menuId}/contents${query ? `?${query}` : ''}`,
    )
  },

  /**
   * Create a daily menu for a date (always draft).
   * A duplicate date returns HTTP 409 with { code: 'daily_exists', existingMenuId }.
   */
  createDaily: (data: CreateDailyMenuRequest) =>
    apiRequest<DailyCreateResult>('/api/v0/admin/menus', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Update menu metadata (name only). */
  update: (menuId: number, data: UpdateMenuRequest) =>
    apiRequest<{ ok: boolean }>(`/api/v0/admin/menus/${menuId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /** Delete a daily menu (soft delete). Master is not deletable. */
  delete: (menuId: number) =>
    apiRequest<{ ok: boolean }>(`/api/v0/admin/menus/${menuId}`, {
      method: 'DELETE',
    }),

  // ========== Menu Operations ==========

  /** Publish a daily menu (validates prices). */
  publish: (menuId: number) =>
    apiRequest<PublishMenuResponse>(`/api/v0/admin/menus/${menuId}/publish`, {
      method: 'POST',
    }),

  /** Unpublish a daily menu (set status back to draft). */
  unpublish: (menuId: number) =>
    apiRequest<{ ok: boolean }>(`/api/v0/admin/menus/${menuId}/unpublish`, {
      method: 'POST',
    }),

  /**
   * Ask for the link that prints this daily menu's Karte in the order app.
   * The server mints a short-lived token scoped to that restaurant and day, so a menu still in
   * draft can be printed the day before without being published to guests.
   */
  printLink: (menuId: number) =>
    apiRequest<MenuPrintLink>(`/api/v0/admin/menus/${menuId}/print-link`, {
      method: 'POST',
    }),

  /** Generate one menu-derived POS layout page (replace-in-place by sourceMenuId). */
  generatePosPage: (restaurantId: number, menuId: number) =>
    apiRequest<GeneratePosPageResponse>(
      `/api/v0/admin/restaurants/${restaurantId}/pos-layout/generate-page`,
      {
        method: 'POST',
        body: JSON.stringify({ menuId }),
      },
    ),

  // ========== Section Operations ==========

  createSection: (menuId: number, data: CreateSectionRequest) =>
    apiRequest<{ ok: boolean; id: number }>(
      `/api/v0/admin/menus/${menuId}/sections`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  updateSection: (
    menuId: number,
    sectionId: number,
    data: UpdateSectionRequest,
  ) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/menus/${menuId}/sections/${sectionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    ),

  deleteSection: (menuId: number, sectionId: number) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/menus/${menuId}/sections/${sectionId}`,
      {
        method: 'DELETE',
      },
    ),

  // ========== Item Operations ==========

  addItem: (menuId: number, sectionId: number, data: AddItemRequest) =>
    apiRequest<AddItemResponse>(
      `/api/v0/admin/menus/${menuId}/sections/${sectionId}/items`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  updateItem: (
    menuId: number,
    sectionId: number,
    productId: number,
    data: UpdateItemRequest,
  ) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/menus/${menuId}/sections/${sectionId}/items/${productId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    ),

  /**
   * Re-files an item into another section of the same menu
   * (CHG-2026-09-menu-editor-redesign). Not delete-then-add: that is two calls without a
   * transaction, and a failed second one loses the item and its override name.
   *
   * 409 `already_in_target_section` when the target already holds the product,
   * 409 `target_section_not_in_menu` when the section belongs to another menu.
   */
  moveItem: (
    menuId: number,
    sectionId: number,
    productId: number,
    toSectionId: number,
    sortOrder?: number,
  ) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/menus/${menuId}/sections/${sectionId}/items/${productId}/section`,
      {
        method: 'PUT',
        body: JSON.stringify({ toSectionId, sortOrder }),
      },
    ),

  deleteItem: (menuId: number, sectionId: number, productId: number) =>
    apiRequest<{ ok: boolean }>(
      `/api/v0/admin/menus/${menuId}/sections/${sectionId}/items/${productId}`,
      {
        method: 'DELETE',
      },
    ),

  bulkAddItemsByCategory: (
    menuId: number,
    sectionId: number,
    categoryCode: string,
  ) =>
    apiRequest<BulkAddItemsResponse>(
      `/api/v0/admin/menus/${menuId}/sections/${sectionId}/items/bulk-by-category`,
      {
        method: 'POST',
        body: JSON.stringify({ categoryCode }),
      },
    ),
}
